// services/ordersListSlice.js
import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "../apiSlice";

// ---------------- Adapter ----------------
const ordersAdapter = createEntityAdapter({
  selectId: (order) => order.id,
});

const initialState = ordersAdapter.getInitialState();

// ---------------- API ----------------
export const ordersListSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getOrdersList: builder.query({
      query: (filters) => {
        const params = [];
        if (filters.status) params.push(`status=${filters.status}`);
        if (filters.search) params.push(`search=${encodeURIComponent(filters.search)}`);
        return params.length ? `orders?${params.join("&")}` : "orders";
      },
      transformResponse: (response) => {
        const normalized = (response.data ?? []).map((item) => ({
          ...item,
          urlLabel: item.url ? item.url.split("/").pop() : "",
        }));
        return ordersAdapter.setAll(initialState, normalized);
      },
      providesTags: (result) => [
        { type: "Orders", id: "LIST" },
        ...(result?.ids ?? []).map((id) => ({ type: "Orders", id })),
      ],
    }),
    getOrderById: builder.query({
      query: (id) => `orders/${id}`,
      transformResponse: (response) => {
        const order = response.data;
        return order
          ? { ...order, urlLabel: order.url ? order.url.split("/").pop() : "" }
          : null;
      },
      providesTags: (result, error, id) => [{ type: "Orders", id }],
    }),
  }),
});

export const { useGetOrdersListQuery, useGetOrderByIdQuery } = ordersListSlice;

// ---------------- Filter → Query Arg ----------------
const selectOrderFilter = (state) => state.orderFilter;

export const selectOrdersQueryArg = createSelector(
  [selectOrderFilter],
  (filter) => ({
    status: filter.status,
    search: filter.search,
  })
);

// ---------------- Adapter Selectors ----------------
const selectOrdersQueryResult = createSelector(
  [selectOrdersQueryArg, (state) => state],
  (filters, state) =>
    ordersListSlice.endpoints.getOrdersList.select(filters)(state)
);

const selectOrdersData = createSelector(
  [selectOrdersQueryResult],
  (queryResult) => queryResult?.data ?? initialState
);

export const {
  selectAll: selectOrders,
  selectById: selectOrderById,
  selectIds: selectOrderIds,
} = ordersAdapter.getSelectors((state) => selectOrdersData(state));
